import * as THREE from 'three';
import { events } from '../core/events.js';
import { groundRadius } from '../world/placement.js';
import { createRescueShip } from '../world/rescueShip.js';
import { blockGameInput } from '../input.js';
import { player, suspendController } from '../player/controller.js';
import { camera } from '../scene.js';
import { setCinematicCamera } from '../camera/follow.js';

// Rescue cutscene — a landing shuttle descends, hovers over the
// commander, fires a tractor beam, and lifts them up inside before
// rising into the sky.

const UP_Y = new THREE.Vector3(0, 1, 0);
const DESCENT_START_ALT = 110;
const HOVER_ALT         = 7.2;          // ship's belly above ground while hovering
const SHIP_BELLY_Y      = 0.0;          // ship-local y of belly (emitter)
const RISE_END_ALT      = HOVER_ALT - 1.4;   // character's final altitude (inside ship)

// Phase table (name, seconds).
// No `launch_hold` — that was a dead beat that read as a cut. Instead,
// `launching` uses a strong ease-in so the first second is a visible
// wind-up, then the ship accelerates. The camera does the same.
const PHASES = [
  ['descending', 6.5],
  ['hovering',   1.6],
  ['beam_on',    1.0],
  ['beaming',    4.4],
  ['beam_off',   0.9],
  ['launching',  6.8],
  ['fading',     3.0],
];

// Cinematic orbit camera.
const ORBIT_RADIUS = 14.5;
const ORBIT_HEIGHT = 4.5;
const ORBIT_RATE   = 0.06;

// Ship self-rotation. The shuttle spins slowly around its own vertical
// axis (landingDir in world space) the whole time it's on screen —
// classic saucer motif.
const SHIP_SPIN_RATE = 0.4;   // rad/s (~23°/s)

// Exponential approach rates. The camera takes over immediately at
// cutscene start, captures its current (follow) pose, and eases toward
// the orbit pose and the ship-tracking lookAt. Separate rates let the
// aim track the ship snappily while the position drifts outward more
// slowly — giving the feel "watching from where I stood, then slowly
// zooming out around the landing."
const CAM_POS_RATE  = 0.55;   // half-life ~1.3 s
const CAM_LOOK_RATE = 2.0;    // half-life ~0.35 s

// Deliberate opening pan. For the first N seconds of the cutscene the
// lookAt is a smoothstep blend from the follow camera's initial aim
// toward the ship — a slow, cinematic "find the subject" sweep. After
// that window the normal exponential tracking takes over.
const OPENING_PAN_DURATION = 2.6;

let sceneRef     = null;
let planetRef    = null;
let characterRef = null;
let shipGroup    = null;
let thrusters    = null;
let navLight     = null;
let emitterMat   = null;
let beamMesh     = null;
let beamMat      = null;

let landingDir       = null;   // the unit direction the ship hovers along
let landingWorldPos  = null;   // the ground point under the hover
let landingQuat      = new THREE.Quaternion();
let tangentA         = new THREE.Vector3();
let tangentB         = new THREE.Vector3();
let fadeEl           = null;

// Flight-path endpoints. Ship doesn't drop straight down — it flies in
// on a long diagonal from high-and-to-one-side, curving in to the hover
// position. That way it enters the player's periphery as they stand
// looking forward, before the camera takes over for the orbit.
const flightStart = new THREE.Vector3();
const flightEnd   = new THREE.Vector3();

let phaseIdx = -1;
let elapsed = 0;
let totalElapsed = 0;

// Exponentially-smoothed camera position + lookAt. Initialised on the
// first frame from the follow camera's current pose so the takeover is
// invisible — there's no snap to a "starting" cinematic position. From
// there each value eases toward the orbit target with its own rate.
const smoothCamPos = new THREE.Vector3();
const smoothLookAt = new THREE.Vector3();
let cameraInitialised = false;

// LookAt at the moment the camera was taken over — the anchor the
// opening pan sweeps *away from*.
const openingPanStartLook = new THREE.Vector3();

// Captured at cutscene start so the character's base orientation during
// beaming matches what they were facing when the cutscene began — no
// rotational pop as the beam picks them up.
const characterStartQuat = new THREE.Quaternion();
let beamingPhaseElapsed = 0;

const _tmp      = new THREE.Vector3();
const _tmpB     = new THREE.Vector3();
const _camPos   = new THREE.Vector3();
const _lookTgt  = new THREE.Vector3();
const _axis     = new THREE.Vector3();
const _q        = new THREE.Quaternion();
const _qSpin    = new THREE.Quaternion();
const _m        = new THREE.Matrix4();

export function initEndingCutscene(scene, planet, character) {
  sceneRef     = scene;
  planetRef    = planet;
  characterRef = character;
  fadeEl       = document.getElementById('cutscene-fade');
  events.on('rescue:arriving', startCutscene);
}

function _phaseName()      { return phaseIdx >= 0 && phaseIdx < PHASES.length ? PHASES[phaseIdx][0] : 'idle'; }
function _phaseDuration()  { return phaseIdx >= 0 && phaseIdx < PHASES.length ? PHASES[phaseIdx][1] : 0; }

function startCutscene() {
  if (phaseIdx >= 0) return;

  // Hover directly above the player so the beam drops straight onto
  // them and we don't have to shuffle them sideways.
  landingDir = player.charPos.clone().normalize();
  const gR = groundRadius(planetRef, landingDir);
  landingWorldPos = landingDir.clone().multiplyScalar(gR);
  landingQuat.setFromUnitVectors(UP_Y, landingDir);

  // Tangent basis for the orbit camera — anchored to the player's
  // current forward so the orbit axes are consistent with the view.
  tangentA.copy(player.charForward);
  tangentA.addScaledVector(landingDir, -tangentA.dot(landingDir));
  if (tangentA.lengthSq() < 1e-4) tangentA.set(1, 0, 0);
  tangentA.normalize();
  tangentB.crossVectors(landingDir, tangentA).normalize();

  // Flight-in path. Start well ahead of the player (in their forward
  // direction so it falls inside the follow camera's view frustum
  // from frame one), offset slightly to one side for a diagonal, and
  // up at cruising altitude. End at the hover point. Keeping the ship
  // mostly ahead-of-player, with modest side offset, means the player
  // can actually see it fly in during the follow-camera hold.
  flightStart
    .copy(landingWorldPos)
    .addScaledVector(tangentA, 135)                    // far ahead
    .addScaledVector(tangentB, 32)                     // gentle side offset
    .addScaledVector(landingDir, 34);                  // cruising altitude
  flightEnd
    .copy(landingDir).multiplyScalar(gR + HOVER_ALT);

  // Build ship at the flight start position.
  shipGroup = createRescueShip();
  const ud = shipGroup.userData;
  thrusters  = ud.thrusters;
  navLight   = ud.navLight;
  emitterMat = ud.emitterMat;
  beamMesh   = ud.beamMesh;
  beamMat    = ud.beamMat;
  shipGroup.quaternion.copy(landingQuat);
  shipGroup.position.copy(flightStart);
  sceneRef.add(shipGroup);
  sceneRef.add(beamMesh);
  for (const g of thrusters) g.visible = true;

  // Block input and take over the player controller. The camera is
  // grabbed on the first cutscene frame from wherever the follow
  // camera left it — see the takeover block in updateEndingCutscene.
  blockGameInput(true);
  suspendController(true);
  cameraInitialised = false;
  characterStartQuat.copy(characterRef.quaternion);
  beamingPhaseElapsed = 0;

  phaseIdx = 0;
  elapsed = 0;
  totalElapsed = 0;
}

// Compute where the cinematic camera *wants* to be this frame (writes
// to outPos) and what it wants to look at (writes to outLook). The
// caller is responsible for easing toward these — this is just the
// instantaneous target.
//
// Position: behind the player along -tangentA (so the player stays in
// frame from behind), with a slow orbit sweep on top. The radius and
// height grow as the ship climbs, gradually pulling the camera back
// for a wider shot without ever leaving the "behind the player" arc.
//
// LookAt: midpoint of the player anchor and the ship. This frames
// both subjects naturally — when the ship is far up and approaching,
// the midpoint is high and ahead, so the camera tilts up toward it;
// once the ship hovers above the player the midpoint sits just
// overhead; during launch the midpoint rises with the ship.
function _computeTargetPose(phaseName, phaseT, outPos, outLook) {
  // Start at angle PI so cos = -1 → behind the player along -tangentA.
  const orbitAngle = Math.PI + totalElapsed * ORBIT_RATE;

  let targetH = ORBIT_HEIGHT;
  let targetR = ORBIT_RADIUS;
  if (phaseName === 'launching' || phaseName === 'fading') {
    const shipAlt = shipGroup.position.length() - groundRadius(planetRef, landingDir);
    const shipClimb = Math.max(0, shipAlt - HOVER_ALT);
    targetH += shipClimb * 0.45;
    const eased = phaseName === 'fading' ? 1 : Math.pow(phaseT, 2);
    targetR += eased * 6.0;
  }

  _tmp.copy(landingDir).multiplyScalar(targetH).add(landingWorldPos);
  outPos.copy(_tmp)
    .addScaledVector(tangentA, Math.cos(orbitAngle) * targetR)
    .addScaledVector(tangentB, Math.sin(orbitAngle) * targetR);

  // LookAt blends from the player↔ship midpoint (keeps both framed as
  // the ship descends) toward the ship itself once the player has
  // been beamed up and the ship climbs out of orbit.
  const shipAlt = shipGroup.position.length() - groundRadius(planetRef, landingDir);
  const shipBias = Math.min(1, Math.max(0, (shipAlt - HOVER_ALT) / 30));
  outLook.copy(landingWorldPos).add(shipGroup.position).multiplyScalar(0.5);
  outLook.lerp(shipGroup.position, shipBias);
}

// Per-phase thruster burn endpoints. Each pair is [startBurn, endBurn],
// and the live burn is linearly interpolated across the phase. The
// endpoint of one phase matches the start of the next, so burn level
// is continuous across phase changes (no more scale pops).
const BURN_ENDPOINTS = {
  descending: [1.0,  0.75],
  hovering:   [0.75, 0.75],
  beam_on:    [0.75, 0.75],
  beaming:    [0.75, 0.75],
  beam_off:   [0.75, 0.85],
  launching:  [0.85, 2.1],
  fading:     [2.1,  2.1],
};

function _burnForPhase(phaseName, phaseT) {
  const ep = BURN_ENDPOINTS[phaseName];
  if (!ep) return 1.0;
  return ep[0] + (ep[1] - ep[0]) * phaseT;
}

function _applyThrusters(burn) {
  // Jitter on intensity (combustion flicker) but scale stays driven by
  // burn so the exhaust length grows smoothly.
  for (const g of thrusters) {
    const jitter = 0.8 + Math.random() * 0.35;
    g.scale.y = burn * (0.95 + Math.random() * 0.12);
    g.material.emissiveIntensity = 3.1 * burn * jitter;
  }
}

// Stretch and position the beam between the ship's belly and the
// ground directly below it. Visibility + intensity are driven by the
// phase.
function _updateBeam() {
  if (!beamMesh) return;
  const gR = groundRadius(planetRef, landingDir);
  const shipBellyWorld = _tmp.copy(landingDir).multiplyScalar(
    gR + (shipGroup.position.length() - gR) + SHIP_BELLY_Y,
  );
  // Length from ship belly to ground along landingDir.
  const beamLen = Math.max(
    0.01,
    shipBellyWorld.length() - gR,
  );
  // Beam centre = midpoint of belly and ground.
  const midAlt = beamLen / 2;
  beamMesh.position.copy(landingDir).multiplyScalar(gR + midAlt);
  beamMesh.scale.set(1, beamLen, 1);
  beamMesh.quaternion.copy(landingQuat);
  beamMat.uniforms.uTime.value = performance.now() / 1000;
}

// Apply character's transform given a world position + in-situ spin.
// The base orientation is whatever the character was wearing when the
// cutscene began (captured in characterStartQuat), so there's no pop
// at the moment beaming takes over their transform. Spin is applied
// around the world-space up (player.charPos) on top of that.
function _applyCharacterAt(worldPos, spinAngle, scale) {
  characterRef.position.copy(worldPos);
  _qSpin.setFromAxisAngle(player.charPos, spinAngle);
  characterRef.quaternion.copy(characterStartQuat).premultiply(_qSpin);
  characterRef.scale.setScalar(scale);
}

export function updateEndingCutscene(dt) {
  if (phaseIdx < 0) return;
  elapsed += dt;
  totalElapsed += dt;

  const phaseName = _phaseName();
  const duration  = _phaseDuration();
  const phaseT    = duration > 0 ? Math.min(1, elapsed / duration) : 1;

  // Nav-light heartbeat.
  if (navLight) {
    const t = performance.now() / 1000;
    navLight.material.emissiveIntensity = 1.3 + Math.pow(
      Math.max(0, Math.sin(t * 2.2)), 10,
    ) * 2.6;
  }

  const gR = groundRadius(planetRef, landingDir);

  // Continuous slow spin around the ship's own vertical axis. Applied
  // on top of landingQuat so the ship stays upright on the planet
  // while rotating in place.
  _q.setFromAxisAngle(UP_Y, totalElapsed * SHIP_SPIN_RATE);
  shipGroup.quaternion.copy(landingQuat).multiply(_q);

  switch (phaseName) {
    case 'descending': {
      // Fly in along a diagonal from `flightStart` to the hover point,
      // decelerating into the destination with an ease-out cubic so
      // the ship settles gently rather than braking hard.
      const eased = 1 - Math.pow(1 - phaseT, 3);
      shipGroup.position.copy(flightStart).lerp(flightEnd, eased);
      break;
    }
    case 'hovering': {
      shipGroup.position.copy(landingDir).multiplyScalar(gR + HOVER_ALT);
      break;
    }
    case 'beam_on': {
      beamMesh.visible = true;
      beamMat.uniforms.uIntensity.value = phaseT;
      emitterMat.emissiveIntensity = 0.4 + 2.0 * phaseT;
      shipGroup.position.copy(landingDir).multiplyScalar(gR + HOVER_ALT);
      break;
    }
    case 'beaming': {
      beamingPhaseElapsed += dt;
      const eased = 0.5 * (1 - Math.cos(phaseT * Math.PI));     // ease in-out
      const alt = 0 + (RISE_END_ALT - 0) * eased;
      _tmp.copy(landingDir).multiplyScalar(gR + alt);
      // Phase-local spin so it starts at 0 rad (no jump from the
      // character's pre-cutscene orientation) and ramps smoothly in.
      // Ease the angular speed in so it's not abrupt at phase start.
      const spinSpeed = Math.min(1.6, beamingPhaseElapsed * 2.4);
      const spin = beamingPhaseElapsed * spinSpeed;
      const scale = 1 - Math.max(0, (phaseT - 0.78) / 0.22);
      _applyCharacterAt(_tmp, spin, scale);

      beamMat.uniforms.uIntensity.value = 1;
      emitterMat.emissiveIntensity = 2.4;
      shipGroup.position.copy(landingDir).multiplyScalar(gR + HOVER_ALT);

      if (phaseT >= 1) characterRef.visible = false;
      break;
    }
    case 'beam_off': {
      beamMat.uniforms.uIntensity.value = 1 - phaseT;
      emitterMat.emissiveIntensity = 0.4 + 2.0 * (1 - phaseT);
      shipGroup.position.copy(landingDir).multiplyScalar(gR + HOVER_ALT);
      if (phaseT >= 1) beamMesh.visible = false;
      break;
    }
    case 'launching': {
      // Gentler ease-in — motion is visible from frame one and ramps
      // smoothly into acceleration. pow(t, 2) avoids the dead creep of
      // a cubic while still preserving zero velocity at the start.
      const eased = Math.pow(phaseT, 2);
      const alt = HOVER_ALT
        + (DESCENT_START_ALT * 1.4 - HOVER_ALT) * eased;
      shipGroup.position.copy(landingDir).multiplyScalar(gR + alt);
      // Start the "The End" fade partway through the climb so the
      // card rises over a ship that's still visibly moving, rather
      // than coming up on a frozen frame.
      if (phaseT >= 0.55 && fadeEl && !fadeEl.classList.contains('visible')) {
        fadeEl.classList.add('visible');
      }
      break;
    }
    case 'fading': {
      if (fadeEl && !fadeEl.classList.contains('visible')) {
        fadeEl.classList.add('visible');
      }
      break;
    }
    default: break;
  }

  // Thrusters run off the continuous burn curve so their scale + glow
  // never jump at phase boundaries.
  _applyThrusters(_burnForPhase(phaseName, phaseT));

  if (beamMesh && beamMesh.visible) _updateBeam();

  // Camera takeover. On the first frame we capture the follow camera's
  // current pose into the smoothed state, so the cinematic starts
  // exactly where the player was already looking. From there we ease
  // toward the orbit target each frame — position drifts slowly
  // outward, and for the first OPENING_PAN_DURATION seconds the lookAt
  // is a smoothstep'd pan from the captured aim toward the ship so the
  // "find the subject" motion reads as a deliberate camera move
  // instead of a snap. After that the lookAt switches to exponential
  // tracking of the ship target.
  if (!cameraInitialised) {
    setCinematicCamera(true);
    smoothCamPos.copy(camera.position);
    const fwd = _tmpB.set(0, 0, -1).applyQuaternion(camera.quaternion);
    smoothLookAt.copy(camera.position).addScaledVector(fwd, 10);
    openingPanStartLook.copy(smoothLookAt);
    cameraInitialised = true;
  }
  _computeTargetPose(phaseName, phaseT, _camPos, _lookTgt);
  const kPos = 1 - Math.exp(-CAM_POS_RATE * dt);
  smoothCamPos.lerp(_camPos, kPos);
  if (totalElapsed < OPENING_PAN_DURATION) {
    const t = totalElapsed / OPENING_PAN_DURATION;
    const s = t * t * (3 - 2 * t);                // smoothstep
    smoothLookAt.copy(openingPanStartLook).lerp(_lookTgt, s);
  } else {
    const kLook = 1 - Math.exp(-CAM_LOOK_RATE * dt);
    smoothLookAt.lerp(_lookTgt, kLook);
  }
  camera.position.copy(smoothCamPos);
  camera.up.copy(landingDir);
  camera.lookAt(smoothLookAt);

  if (phaseT >= 1) {
    phaseIdx += 1;
    elapsed = 0;
    if (phaseIdx >= PHASES.length) phaseIdx = PHASES.length - 1;
  }
}
